import type { Corner, PlannedShot } from "../types"
import type { TurnJudgement } from "../motion/turn"
import type { ViewClass, ViewProbs } from "./head"
import type { EndHint } from "./taillights"

// One verdict on a photo, from everything the phone knows: the car-view brain
// (what the photo shows), the turn since the last whole-car photo (where the
// person walked), and the taillights (front or back). It only speaks up when
// it's sure; otherwise the walk just carries on. Pure; unit-tested.
//
// Thresholds come from scripts/car-brain/REPORT.md: at 90% the brain wrongly
// objects to about 3 in 100 right photos, so "wrong corner" on its own needs
// that much; with a second signal agreeing, 75% is enough.

export type PhoneVerdict =
  | { kind: "ok"; confidence: number }
  // Whole car asked for, this is another corner / end / side.
  | { kind: "wrong-corner"; seen: SeenAs }
  // Whole car asked for, this is a close-up.
  | { kind: "too-close" }
  // A close-up asked for, this shows the whole car.
  | { kind: "too-far" }
  // Same spot as the last whole-car photo.
  | { kind: "same-spot" }
  | { kind: "unsure" }

// What the photo looks like, in walk terms.
export interface SeenAs {
  end: "front" | "rear" | null
  side: "left" | "right" | null
}

export const VERDICT = {
  OK: 0.6,
  OK_ANYWAY: 0.85,
  WRONG_ALONE: 0.9,
  WRONG_BACKED: 0.75,
  CLOSE: 0.8,
  FAR: 0.85,
} as const

const NEAR: Record<Corner, readonly ViewClass[]> = {
  FL: ["FL", "front", "left"],
  FR: ["FR", "front", "right"],
  RL: ["RL", "rear", "left"],
  RR: ["RR", "rear", "right"],
}
const WHOLE: readonly ViewClass[] = ["front", "rear", "left", "right", "FL", "FR", "RL", "RR"]

const END_OF: Partial<Record<ViewClass, "front" | "rear">> = { front: "front", FL: "front", FR: "front", rear: "rear", RL: "rear", RR: "rear" }
const SIDE_OF: Partial<Record<ViewClass, "left" | "right">> = { left: "left", FL: "left", RL: "left", right: "right", FR: "right", RR: "right" }

export function cornerEnd(c: Corner): "front" | "rear" {
  return c === "FL" || c === "FR" ? "front" : "rear"
}
export function cornerSide(c: Corner): "left" | "right" {
  return c === "FL" || c === "RL" ? "left" : "right"
}

const sum = (p: ViewProbs, cs: readonly ViewClass[]) => cs.reduce((s, c) => s + p[c], 0)

// The most likely whole-car view outside the asked corner's neighbourhood.
function seenAs(p: ViewProbs, asked: Corner): SeenAs {
  let best: ViewClass = "front"
  let bestP = -1
  for (const c of WHOLE) {
    if (NEAR[asked].includes(c)) continue
    if (p[c] > bestP) {
      bestP = p[c]
      best = c
    }
  }
  return { end: END_OF[best] ?? null, side: SIDE_OF[best] ?? null }
}

export interface ShotEvidence {
  shot: Pick<PlannedShot, "kind" | "corner">
  probs: ViewProbs | null
  // Turn since the last whole-car photo of another corner (null: unknown).
  turn: TurnJudgement | null
  taillights: EndHint
}

export function judgeShot({ shot, probs, turn, taillights }: ShotEvidence): PhoneVerdict {
  if (shot.kind === "wide" && shot.corner) {
    const asked = shot.corner
    if (probs) {
      const close = probs.closeup + probs.sticker
      if (close >= VERDICT.CLOSE) return { kind: "too-close" }
      const ok = sum(probs, NEAR[asked])
      // A turn reading can't overrule a brain that is sure it's the right
      // view (sensors restart, drift); it only tips a maybe.
      if (ok >= VERDICT.OK_ANYWAY || (ok >= VERDICT.OK && turn !== "same-spot")) return { kind: "ok", confidence: ok }
      const wrong = 1 - ok - close - probs.other
      const seen = seenAs(probs, asked)
      const lampsAgree =
        taillights === "rear" && cornerEnd(asked) === "front" && seen.end === "rear"
      const turnAgrees = turn === "same-spot" || turn === "elsewhere"
      // Front against back is the brain's surest call (REPORT.md); a turn that
      // fits the walk can only overrule a left/right disagreement.
      const otherEnd = !!seen.end && seen.end !== cornerEnd(asked)
      if (wrong >= VERDICT.WRONG_ALONE && (turn !== "expected" || otherEnd)) return { kind: "wrong-corner", seen }
      if (wrong >= VERDICT.WRONG_BACKED && (lampsAgree || turnAgrees)) return { kind: "wrong-corner", seen }
    }
    if (turn === "same-spot") return { kind: "same-spot" }
    return { kind: "unsure" }
  }
  if (shot.kind === "closeup" && probs) {
    if (sum(probs, WHOLE) >= VERDICT.FAR) return { kind: "too-far" }
    return { kind: "ok", confidence: probs.closeup + probs.sticker }
  }
  return { kind: "unsure" }
}

// Live, before the photo: is the camera on the corner it asked for? The
// same rules as the verdict after the photo (judgeShot), so the two never
// disagree, plus one more: standing square to the front (or the side)
// instead of at the corner, which is fixed with a step, not a walk.
export type LiveViewJudgement =
  | { kind: "right" }
  | { kind: "unsure" }
  | { kind: "wrong"; seen: SeenAs }
  // Too square-on: step toward the side (seeing only the end) or toward the
  // end (seeing only the side).
  | { kind: "angle"; toward: "side" | "end" }

export const ANGLE = {
  // The end (or the side) alone at least this sure…
  FLAT: 0.6,
  // …and the corner itself this unlikely. Provisional: tuned on real walks
  // (scripts/walks/REPORT.md).
  CORNER_MAX: 0.25,
} as const

export function judgeLiveView(asked: Corner, probs: ViewProbs | null, turn: TurnJudgement | null): LiveViewJudgement {
  if (!probs) return { kind: "unsure" }
  if (probs[asked] < ANGLE.CORNER_MAX) {
    const end = cornerEnd(asked) === "front" ? probs.front : probs.rear
    const side = cornerSide(asked) === "left" ? probs.left : probs.right
    if (end >= ANGLE.FLAT) return { kind: "angle", toward: "side" }
    if (side >= ANGLE.FLAT) return { kind: "angle", toward: "end" }
  }
  const verdict = judgeShot({ shot: { kind: "wide", corner: asked }, probs, turn, taillights: null })
  if (verdict.kind === "ok") return { kind: "right" }
  if (verdict.kind === "wrong-corner") return { kind: "wrong", seen: verdict.seen }
  return { kind: "unsure" }
}

